import { h } from 'preact';
import Image from 'utils/image';
import { useRef, useState, useEffect } from 'preact/hooks';
import PlayIconCircle from 'utils/icon/icon-play-circle.svg';
import PlayIcon from 'utils/icon/icon-play.svg';
import PauseIcon from 'utils/icon/icon-pause.svg';
import useInView from 'utils/in-view';

const Media = ({
  mediaContentType,
  previewImage,
  sources,
  srcWidths,
  className,
  parentClass,
  controlClass,
  alt,
  onClick,
  lazyLoad,
  controlIndex,
  justPlayButton,
  enablePhotoSwipe = false,
  fetchPriority,
  ...props
}) => {
  if (mediaContentType === 'IMAGE') {
    const sourcesCopy = [...(sources || [])];
    const largestSource = sourcesCopy.sort((a, b) => b.width - a.width)[0];
    const imageUrl = largestSource?.url || previewImage.src;
    const imageWidth = largestSource?.width || previewImage.width;
    const imageHeight = largestSource?.height || previewImage.height;
    if (enablePhotoSwipe) {
      return (
        <div className={parentClass}>
          <a
            href={imageUrl}
            data-pswp-width={imageWidth}
            data-pswp-height={imageHeight}
            data-pswp-src={imageUrl}
            data-pswp-alt={alt || previewImage.alt}
            target="_blank"
            rel="noreferrer"
            className="absolute inset-0 block"
            onClick={(e) => {
              e.preventDefault();
              if (onClick) onClick();
            }}
          >
            <Image
              {...previewImage}
              lazyLoad={lazyLoad}
              srcWidths={srcWidths}
              classes={className}
              fetchPriority={fetchPriority}
            />
          </a>
        </div>
      );
    }

    return (
      <div className={parentClass} onClick={() => onClick && onClick()}>
        <Image
          {...previewImage}
          lazyLoad={lazyLoad}
          srcWidths={srcWidths}
          classes={className}
          fetchPriority={fetchPriority}
        />
      </div>
    );
  }

  if (mediaContentType === 'VIDEO') {
    return (
      <Video
        previewImage={previewImage}
        sources={sources}
        className={className}
        lazyLoad={lazyLoad}
        parentClass={parentClass}
        controlClass={controlClass}
        controlIndex={controlIndex}
        onClick={onClick}
        justPlayButton={justPlayButton}
        alt={alt}
        {...props}
      />
    );
  }

  return null;
};

const Video = ({
  previewImage,
  sources,
  className,
  parentClass = '',
  lazyLoad,
  onClick,
  controlClass = '',
  justPlayButton,
  alt,
  controlIndex,
}) => {
  const video = useRef(null);
  const [paused, setPaused] = useState(false);
  const [ref, inView, disconnect] = useInView({ rootMargin: '100px' });
  const [visible, setVisible] = useState(!lazyLoad);

  useEffect(() => {
    if (inView) {
      setVisible(true);
      disconnect();
    }
  }, [inView]);

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }
    toggleVideo();
  };

  const toggleVideo = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (video.current.paused) {
      video.current.play();
    } else {
      video.current.pause();
    }
    setPaused(video.current.paused);
  };
  const randomVideoId = `video_${Math.floor(Math.random() * 999)}`
  return (
    <div className={`${parentClass}`} ref={ref}>
      <video
        className={`absolute inset-0 w-full h-full object-cover ${className}`}
        poster={previewImage.url || previewImage.src}
        playsInline
        ref={video}
        loop
        muted
        autoPlay
        onClick={handleClick}
        aria-describedby={`${randomVideoId}`}
        aria-label={alt}
      >
        {visible &&
          sources.map((source, index) => (
            <source
              src={source.url}
              key={index}
              type={`video/${source.format}`}
            />
          ))}
      </video>
      <div className='sr-only' id={`${randomVideoId}`}>{alt}</div>
      <button
        className={`z-10 absolute right-5 bottom-5 
         w-8 h-8 rounded-full
         flex items-center justify-center ${controlClass}`}
        onClick={toggleVideo}
        style={{ opacity: 0 }}
        tabIndex={controlIndex || '0'}
        aria-label={!paused ? 'pause decorative video' : 'play decorative video'}
      >
        {
          (justPlayButton === "true") ? 
            (<PlayIconCircle className={`w-8 h-auto ${(justPlayButton !== "true")? 'hidden' : ''}`} />)
          : (
              <div>
                {!paused && <PauseIcon className="w-8 h-auto" />}
                {paused && <PlayIcon className="w-8 h-auto" />}
              </div>
            )
        }
      </button>
    </div>
  );
};

export default Media;
